import { useState, useCallback } from 'react';
import axios from '../config/api';
import { useAuth } from '@clerk/clerk-react';
import { logError, logInfo } from '../utils/logger';
import { useUploadDisabled } from './useUploadDisabled';
import { validateFileType, validateFileSize, getFileAcceptString, formatFileSize, MAX_FILE_SIZE_MB } from '../constants/fileTypes';

interface UploadedSession {
  id: string;
  name: string;
  created_at: string;
  has_analysis: boolean;
  latest_job_id: string | null;
  latest_job_status: string | null;
}

interface UseFileUploadProps {
  onUploadStart?: () => void;
  onUploadComplete?: (session: UploadedSession) => void;
  onUploadSuccess?: (sessionId: string) => void;
}

export const useFileUpload = ({ onUploadStart, onUploadComplete, onUploadSuccess }: UseFileUploadProps = {}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisabledModal, setShowDisabledModal] = useState(false);
  const { getToken } = useAuth();
  const { isDisabled: isUploadDisabled, isLoading: isDisabledLoading } = useUploadDisabled();

  const getAuthToken = useCallback(async () => {
    try {
      return await getToken();
    } catch (error) {
      console.error('Failed to get access token:', error);
      throw error;
    }
  }, [getToken]);

  const resetState = useCallback(() => {
    setUploadProgress(0);
    setIsUploading(false);
    setError(null);
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    resetState();

    if (!validateFileType(file)) {
      setError('Invalid file type. Please upload a CSV, VBO, LOG, or GPC file.');
      return;
    }

    if (!validateFileSize(file)) {
      const fileSize = formatFileSize(file.size);
      setError(`File size (${fileSize}) exceeds the ${MAX_FILE_SIZE_MB}MB limit. Please choose a smaller file.`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('compute_lap_times', 'true');

    setIsUploading(true);
    onUploadStart?.();

    try {
      const token = await getAuthToken();
      const response = await axios.post('/api/sessions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress(percentCompleted);
        },
      });

      const session: UploadedSession = {
        id: response.data.id,
        name: response.data.name ?? response.data.id,
        created_at: response.data.created_at ?? new Date().toISOString(),
        has_analysis: response.data.has_analysis ?? false,
        latest_job_id: response.data.latest_job_id ?? null,
        latest_job_status: response.data.latest_job_status ?? null,
      };
      logInfo('Session created successfully', { sessionId: session.id }, 'useFileUpload');

      setIsUploading(false);

      // Refresh sidebar and navigate to the new session
      onUploadSuccess?.(session.id);
      onUploadComplete?.(session);
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.response?.data?.message || 'Upload failed'
        : 'An unexpected error occurred during upload';

      logError('File upload failed', {
        fileName: file.name,
        error: errorMessage,
        status: axios.isAxiosError(error) ? error.response?.status : undefined
      }, 'useFileUpload');

      setError(errorMessage);
      setUploadProgress(0);
      setIsUploading(false);
    }
  }, [onUploadStart, onUploadSuccess, onUploadComplete, resetState, getAuthToken]);

  const openFileDialog = useCallback(() => {
    if (isDisabledLoading) return;

    if (isUploadDisabled) {
      setShowDisabledModal(true);
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = getFileAcceptString();
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        uploadFile(file);
      }
    };
    input.click();
  }, [uploadFile, isUploadDisabled, isDisabledLoading]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    uploadProgress,
    isUploading,
    error,
    showDisabledModal,
    setShowDisabledModal,
    resetState,
    clearError,
    uploadFile,
    openFileDialog,
    isLoading: isUploading,
  };
};
